// // Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { ERC20Wei, ERC20WeiSigned } from './conversions'
import { PoolTypeEnum, yupErc20WeiOptional } from './Enums'
import { PoolBaseAccountCTOSchemaValues } from './PoolBaseAccountCTO'
import { PoolDynamicAccountOnlyCTOSchemaValues } from './PoolDynamicAccountCTO'
import { PoolEventCTO, PoolEventSchema } from './PoolEventCTO'
import { ArrayTypeEnum, EthAddr, ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

export const VaultAccountCTOSchemaValues = {
  accountAddr: PoolBaseAccountCTOSchemaValues.accountAddr,
  assetsDeposited: PoolDynamicAccountOnlyCTOSchemaValues.assetsDeposited,
  assetsWithdrawn: PoolDynamicAccountOnlyCTOSchemaValues.assetsWithdrawn,
  benchmarkRate: PoolBaseAccountCTOSchemaValues.benchmarkRate,
  blockNumber: PoolBaseAccountCTOSchemaValues.blockNumber,
  currencyLabel: PoolBaseAccountCTOSchemaValues.currencyLabel,
  dayNumber: PoolBaseAccountCTOSchemaValues.dayNumber,
  displayName: PoolBaseAccountCTOSchemaValues.displayName,
  feeRate: yup
    .number()
    .required()
    .label('All Fees')
    .default(0)
    .meta(
      toFieldMetaData({
        propName: 'feeRate',
        description: 'Total of all fees paid by the lender on the vault. ',
        stringType: StringTypeEnum.OFFCHAIN_RATE,
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 30,
      })
    ),

  gainLoss: PoolDynamicAccountOnlyCTOSchemaValues.gainLoss,
  gainLossInDay: PoolDynamicAccountOnlyCTOSchemaValues.gainLossInDay,
  id: PoolBaseAccountCTOSchemaValues.id,
  interestRate: PoolDynamicAccountOnlyCTOSchemaValues.interestRate,
  liquidityAssetAddr: PoolBaseAccountCTOSchemaValues.liquidityAssetAddr,
  liquidityTokenSymbol: PoolBaseAccountCTOSchemaValues.liquidityTokenSymbol,
  maxRedeemRequest: PoolDynamicAccountOnlyCTOSchemaValues.maxRedeemRequest,
  maxWithdrawRequest: PoolDynamicAccountOnlyCTOSchemaValues.maxWithdrawRequest,
  poolAddr: PoolBaseAccountCTOSchemaValues.poolAddr,
  poolType: PoolBaseAccountCTOSchemaValues.poolType,
  principalEarningInterest: PoolDynamicAccountOnlyCTOSchemaValues.principalEarningInterest,
  requestedAssetsOf: PoolDynamicAccountOnlyCTOSchemaValues.requestedAssetsOf,
  requestedSharesOf: PoolDynamicAccountOnlyCTOSchemaValues.requestedSharesOf,
  timeOfDay: PoolBaseAccountCTOSchemaValues.timeOfDay,
  timestamp: PoolBaseAccountCTOSchemaValues.timestamp,
  timestampDateString: PoolBaseAccountCTOSchemaValues.timestampDateString,
  timestampString: PoolBaseAccountCTOSchemaValues.timestampString,
  tokenBalance: PoolBaseAccountCTOSchemaValues.tokenBalance,
  vaultName: PoolBaseAccountCTOSchemaValues.vaultName,
  poolEvents: yup
    .array(PoolEventSchema)
    .required()
    .default([])
    .label('Vault Events')
    .meta(
      toFieldMetaData({
        arrayType: ArrayTypeEnum.VAULT_EVENT,
        propName: 'poolEvents',
        description: 'Vault Events on day ',
        columnWidth: 250,
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 3,
      })
    ),
  rewardsAccruedInDay: yupErc20WeiOptional.label('Rewards Accrued in Day').meta(
    toFieldMetaData({
      propName: 'rewardsAccruedInDay',
      description: 'Rewards Accrued in Day',
      stringType: StringTypeEnum.STABLE_COINS,
      columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      columnOrder: 16,
    })
  ),
  rewardsAccrued: yupErc20WeiOptional.label('Rewards Accrued').meta(
    toFieldMetaData({
      propName: 'rewardsAccrued',
      description: 'Rewards Accrued',
      stringType: StringTypeEnum.STABLE_COINS,
      columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      columnOrder: 17,
    })
  ),
  rewardsDistributed: yupErc20WeiOptional.label('Rewards Distributed').meta(
    toFieldMetaData({
      propName: 'rewardsDistributed',
      description: 'Rewards Distributed',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
}

export const VaultAccountCTOSchema = yup.object(VaultAccountCTOSchemaValues)
export const VaultAccountCTOSchemaValuesNoRewards = {
  ...VaultAccountCTOSchemaValues,
  rewardsAccruedInDay: yupErc20WeiOptional.label('Rewards Accrued in Day').meta(
    toFieldMetaData({
      propName: 'rewardsAccruedInDay',
      description: 'Rewards Accrued in Day',
      stringType: StringTypeEnum.STABLE_COINS,
      columnVisibility: ReportingColumnVisibilityEnum.Disabled,
    })
  ),
  rewardsAccrued: yupErc20WeiOptional.label('Rewards Accrued').meta(
    toFieldMetaData({
      propName: 'rewardsAccrued',
      description: 'Rewards Accrued',
      stringType: StringTypeEnum.STABLE_COINS,
      columnVisibility: ReportingColumnVisibilityEnum.Disabled,
    })
  ),
  rewardsDistributed: yupErc20WeiOptional.label('Rewards Distributed').meta(
    toFieldMetaData({
      propName: 'rewardsDistributed',
      description: 'Rewards Distributed',
      stringType: StringTypeEnum.STABLE_COINS,
      columnVisibility: ReportingColumnVisibilityEnum.Disabled,
    })
  ),
}

export const VaultAccountCTOSchemaNoRewards = yup.object(VaultAccountCTOSchemaValuesNoRewards)
export interface VaultAccountCTO {
  accountAddr: EthAddr
  assetsDeposited: ERC20Wei
  assetsWithdrawn: ERC20Wei
  benchmarkRate: string
  blockNumber: number
  currencyLabel: string
  dayNumber: number
  displayName?: string | undefined
  feeRate: number
  gainLoss: ERC20WeiSigned
  gainLossInDay: ERC20WeiSigned
  id: string
  interestRate: ERC20Wei
  liquidityAssetAddr: EthAddr
  liquidityTokenSymbol: string
  maxRedeemRequest: ERC20Wei
  maxWithdrawRequest: ERC20Wei
  poolAddr: EthAddr
  poolType: PoolTypeEnum
  principalEarningInterest: ERC20Wei
  requestedAssetsOf: ERC20Wei
  requestedSharesOf: ERC20Wei
  timeOfDay: number
  timestamp: number
  timestampDateString: string
  timestampString: string
  tokenBalance: ERC20Wei
  vaultName: string
  poolEvents: PoolEventCTO[]
  rewardsAccruedInDay?: ERC20Wei
  rewardsAccrued?: ERC20Wei
  rewardsDistributed?: ERC20Wei
}
type VaultAccountCTOInfer = yup.InferType<typeof VaultAccountCTOSchema>

export const _testAPoolBaseOverview: VaultAccountCTO = {} as unknown as VaultAccountCTOInfer
export const _testBPoolBaseOverview: VaultAccountCTOInfer = {} as unknown as VaultAccountCTO
